<script>
import axios from "axios";
import Swal from "sweetalert2";

export default {
  props: ['department', 'dateRange'],
  data() {
    return {
      sourcesData: [],
      totalQtyClients: 0,
      isLoading: false,
      totalQtyProspects: 0,
      chartSeries: [
        {
          name: "Clients",
          data: []
        },
        {
          name: "Prospects",
          data: []
        }
      ],
      chartOptions: {
        chart: {
          type: "bar",
          height: 300,
          toolbar: {
            show: false
          }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "40%",
            endingShape: "rounded"
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["transparent"]
        },
        colors: ["#0ab39c", "#f7b84b"],
        xaxis: {
          categories: [],
          labels: {
            style: {
              colors: "#878a99",
              fontSize: "11px"
            }
          }
        },
        yaxis: {
          labels: {
            style: {
              colors: "#878a99",
              fontSize: "11px"
            }
          }
        },
        grid: {
          borderColor: "#e9ebec",
          strokeDashArray: 4
        },
        fill: {
          opacity: 1
        },
        legend: {
          position: "top",
          horizontalAlign: "right",
          fontSize: "12px",
          labels: {
            colors: "#878a99"
          }
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val + " records";
            }
          }
        }
      }
    };
  },
  methods: {
    async getRegistriesReport() {
      if (!this.dateRange || !this.dateRange.includes(" to ")) {
        return;
      }

      const data = {
        token: localStorage.getItem('jwt'),
        dateRange: this.dateRange,
        department: this.department
      };

      this.isLoading = true;
      await axios
        .post('api/management/reportAgents', data)
        .then(response => {
          this.totalQtyClients = 0;
          this.totalQtyProspects = 0;
          this.sourcesData = response.data;

          const categories = [];
          const clientsData = [];
          const prospectsData = [];

          for (var row in this.sourcesData) {
            var value = this.sourcesData[row];
            this.totalQtyProspects += value.qtyProspects;
            this.totalQtyClients += value.qtyClients;

            categories.push(value.name);
            clientsData.push(value.qtyClients);
            prospectsData.push(value.qtyProspects);
          }

          // Emit to parent
          this.$emit('update-totals', {
            clients: this.totalQtyClients,
            prospects: this.totalQtyProspects
          });

          // Update Chart Data
          this.chartSeries = [
            {
              name: "Clients",
              data: clientsData
            },
            {
              name: "Prospects",
              data: prospectsData
            }
          ];

          this.chartOptions = {
            ...this.chartOptions,
            xaxis: {
              ...this.chartOptions.xaxis,
              categories: categories
            }
          };
        })
        .catch(error => {
          if (error.response) {
            if (error.response.data.detail === 'Your session is expired!') {
              this.$router.push('logout');
            }
            Swal.fire("Oops!", "Something went wrong, please contact your SysAdmin.", "error");
          }
        });
      this.isLoading = false;
    },
    numberWithCommas(x) {
      x = x.toString();
      var pattern = /(-?\d+)(\d{3})/;
      while (pattern.test(x))
        x = x.replace(pattern, "$1,$2");
      return x;
    }
  },
  watch: {
    department: {
      handler() {
        this.getRegistriesReport();
      }
    },
    dateRange: {
      handler() {
        this.getRegistriesReport();
      }
    }
  },
  async mounted() {
    await this.getRegistriesReport();
  }
};
</script>

<template>
  <div class="card card-height-100 border-0 shadow-sm rounded-3">
    <div class="card-header align-items-center d-flex border-0 bg-transparent pt-4 px-4 pb-0">
      <h4 class="card-title mb-0 flex-grow-1 fw-bold">Registries Added By Agent</h4>
    </div>
    
    <div class="card-body px-4">
      <!-- Summary metrics -->
      <div class="row align-items-center mb-4">
        <div class="col-sm-8">
          <h6 class="text-muted text-uppercase fw-semibold text-truncate fs-12 mb-3">Total Clients and Prospects Added</h6>
          <div class="row">
            <div class="col-6 border-end border-light">
              <span class="text-muted fs-12 d-block mb-1">Clients</span>
              <h4 class="fs-20 mb-0 fw-bold text-success">{{ numberWithCommas(this.totalQtyClients) }}</h4>
            </div>
            <div class="col-6 ps-3">
              <span class="text-muted fs-12 d-block mb-1">Prospects</span>
              <h4 class="fs-20 mb-0 fw-bold text-warning">{{ numberWithCommas(this.totalQtyProspects) }}</h4>
            </div>
          </div>
        </div>
      </div>

      <!-- Bar Chart -->
      <div class="mb-4" v-if="chartSeries[0].data.length > 0 || chartSeries[1].data.length > 0">
        <apexchart
          type="bar"
          height="300"
          :options="chartOptions"
          :series="chartSeries"
        ></apexchart>
      </div>
      <div v-else class="text-center py-5 bg-light rounded-3 mb-4">
        <i class="ri-bar-chart-line fs-32 text-muted mb-2 d-block"></i>
        <p class="text-muted mb-0 fs-13">No registries data available.</p>
      </div>

      <!-- Agent Breakdown Table -->
      <div class="table-responsive table-card mt-3 pt-2 border-top border-light" v-if="Object.keys(sourcesData).length > 0">
        <table class="table table-borderless table-centered table-nowrap align-middle mb-0">
          <thead class="text-muted table-light fs-12">
            <tr>
              <th scope="col" style="width: 50%;">Agent Name</th>
              <th scope="col" class="text-center">Clients</th>
              <th scope="col" class="text-center">Prospects</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in sourcesData" :key="index">
              <td>
                <a href="javascript:void(0);" class="link-secondary fw-semibold">{{ row.name }}</a>
              </td>
              <td class="text-center text-success fw-medium">{{ row.qtyClients }}</td>
              <td class="text-center text-warning fw-medium">{{ row.qtyProspects }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>